import React from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { CrawlerPicker } from '@prerender/design-system/CrawlerPicker';
import { clampDateRange, getPresetRange } from '@prerender/design-system/DateRangePicker';

import { FilterBar } from '~components/Filters';
import MoreActionsDropdown, {
  type MoreActionsExportConfig,
} from '~components/Filters/MoreActionsDropdown';
import PlanGatedDateRangePicker from '~components/Filters/PlanGatedDateRangePicker';
import { type DatePickerEventMap, toDatePickerTracker } from '~components/Filters/datePickerEvents';
import type { NoCrawlersSelection } from '~features/crawler-filter/CrawlerFilterHooks';
import { DomainFilter } from '~features/domain-manager/filter';
import { UpgradePlanProvider } from '~features/UpgradePlan';
import { useCurrentPlan } from '../../../../hooks/usePrerenderUser';
import { useAiInsightsChartDescriptionsState } from '../../shared';
import useQueryStringParameters from '../../../../utils/useQueryStringParameters';
import { DISCOVERABILITY_CRAWLER_TAXONOMY } from '../DiscoverabilityCrawlerTaxonomy';
import {
  buildDiscoverabilityQueryParameters,
  CLEARED_SCAN_SCOPE,
  DISCOVERABILITY_DEFAULT_PERIOD,
  DISCOVERABILITY_DEFAULT_PRESET,
  QueryParamNames,
  type DiscoverabilityQueryParams,
} from '../DiscoverabilityQueryParameters';
import { useDiscoverabilityCrawlerFilter } from '../hooks/useDiscoverabilityCrawlerFilter';
import type { DiscoverabilityAnalyticsEvent } from '../DiscoverabilityAnalyticsEvents';
import useTrackDiscoverabilityEvent from '../hooks/useTrackDiscoverabilityEvent';

const ALL_DOMAINS_LABEL = 'All Domains';

const DISCOVERABILITY_DATE_PICKER_EVENTS = {
  PresetClicked: 'Discoverability: Preset Clicked',
  PeriodStepped: 'Discoverability: Period Stepped',
  PeriodReset: 'Discoverability: Period Reset',
} as const satisfies Record<keyof DatePickerEventMap, DiscoverabilityAnalyticsEvent>;

function formatDateRangeValue(from: Date, to: Date): string {
  return `${dayjs(from).format('YYYY-MM-DD')} to ${dayjs(to).format('YYYY-MM-DD')}`;
}

type DiscoverabilityFiltersProps = {
  // Owned by DiscoverabilityView: the readiness signals that gate the export
  // (status/KPI loading, no-activity verdict) live there, not on the filter row.
  export?: MoreActionsExportConfig;
  crawlerSelection: NoCrawlersSelection;
};

function DiscoverabilityFilters({ export: exportConfig, crawlerSelection }: DiscoverabilityFiltersProps) {
  const { noteOwnNavigation } = crawlerSelection;
  const { pickerCategories, pickerSelection, pickCrawlers } = useDiscoverabilityCrawlerFilter(crawlerSelection);
  const currentPlan = useCurrentPlan();
  const trackEvent = useTrackDiscoverabilityEvent();
  const [chartDescriptionsEnabled, setChartDescriptionsEnabled] = useAiInsightsChartDescriptionsState();
  const { queryStrings, updateMultipleParameters } =
    useQueryStringParameters<DiscoverabilityQueryParams>(buildDiscoverabilityQueryParameters);
  const { search } = useLocation();
  // A scan-scope deep link carries BOTH absolute bounds; the backend applies the
  // scan window only when both are present, so a lone bound is not scan scope and
  // must not suppress the retention clamp.
  const isInScanScope = React.useMemo(() => {
    const scanScopeParams = new URLSearchParams(search);
    return (
      scanScopeParams.has('dateTimeFrom') && scanScopeParams.has('dateTimeTo')
    );
  }, [search]);

  const [defaultDateFrom, defaultDateTo] = React.useMemo<[Date, Date]>(() => {
    const { from, end } = getPresetRange(DISCOVERABILITY_DEFAULT_PRESET);
    return clampDateRange(from.toDate(), end.toDate(), currentPlan.dataRetentionDays);
  }, [currentPlan.dataRetentionDays]);

  const urlDateFrom = queryStrings[QueryParamNames.DateFrom];
  const urlDateTo = queryStrings[QueryParamNames.DateTo];

  React.useEffect(() => {
    if (!urlDateFrom || !urlDateTo || isInScanScope) return;
    const [adjustedFrom, adjustedTo] = clampDateRange(
      urlDateFrom,
      urlDateTo,
      currentPlan.dataRetentionDays
    );
    const datesChanged =
      adjustedFrom.getTime() !== urlDateFrom.getTime() ||
      adjustedTo.getTime() !== urlDateTo.getTime();
    if (datesChanged) {
      noteOwnNavigation();
      updateMultipleParameters({ dateFrom: adjustedFrom, dateTo: adjustedTo });
    }
  }, [urlDateFrom, urlDateTo, currentPlan.dataRetentionDays, isInScanScope, updateMultipleParameters, noteOwnNavigation]);

  // A URL range is honored only when BOTH bounds are present; a lone bound falls
  // back to the default window for both, so the picker never shows a range the
  // data (which is likewise all-or-nothing in useDiscoverabilitySearchParams)
  // doesn't reflect. (An unparseable date never reaches here: the query-string
  // parser drops it, so urlDateFrom/urlDateTo are a valid Date or undefined.)
  const hasUrlDateRange = urlDateFrom !== undefined && urlDateTo !== undefined;
  const dateFrom = hasUrlDateRange ? urlDateFrom : defaultDateFrom;
  const dateTo = hasUrlDateRange ? urlDateTo : defaultDateTo;
  const selectedDomain = queryStrings[QueryParamNames.Domain];

  const onDomainSelected = (domain?: string) => {
    trackEvent('Discoverability: Filter Changed', {
      filter_name: 'domain',
      old_value: selectedDomain ?? ALL_DOMAINS_LABEL,
      new_value: domain ?? ALL_DOMAINS_LABEL,
    });
    noteOwnNavigation();
    updateMultipleParameters({
      domain,
      ...CLEARED_SCAN_SCOPE,
    });
  };

  const onCrawlerPickerOpenChange = (open: boolean) => {
    if (open) trackEvent('Discoverability: Crawler Picker Opened');
  };

  const onDatePickerEvent = React.useMemo(
    () =>
      toDatePickerTracker((...[event, properties]) =>
        trackEvent(DISCOVERABILITY_DATE_PICKER_EVENTS[event], properties)
      ),
    [trackEvent]
  );

  const onDatesSelected = (from: Date, to: Date) => {
    trackEvent('Discoverability: Filter Changed', {
      filter_name: 'date_range',
      old_value: formatDateRangeValue(dateFrom, dateTo),
      new_value: formatDateRangeValue(from, to),
    });
    noteOwnNavigation();
    updateMultipleParameters({
      dateFrom: from,
      dateTo: to,
      ...CLEARED_SCAN_SCOPE,
    });
  };

  const onChartDescriptionsChanged = (enabled: boolean) => {
    setChartDescriptionsEnabled(enabled);
    trackEvent('Discoverability: Chart Descriptions Toggled', {
      action: enabled ? 'enabled' : 'disabled',
    });
  };

  return (
    <UpgradePlanProvider>
      <FilterBar>
        <DomainFilter domain={selectedDomain} onDomainChange={onDomainSelected} />

        <CrawlerPicker
          taxonomy={DISCOVERABILITY_CRAWLER_TAXONOMY}
          categories={pickerCategories}
          value={pickerSelection}
          onChange={pickCrawlers}
          onOpenChange={onCrawlerPickerOpenChange}
        />

        <PlanGatedDateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onDatesSelected}
          defaultPeriod={DISCOVERABILITY_DEFAULT_PERIOD}
          trackEvent={onDatePickerEvent}
          placement="bottomRight"
        />

        <MoreActionsDropdown
          export={exportConfig}
          chartDescriptions={{
            enabled: chartDescriptionsEnabled,
            onChange: onChartDescriptionsChanged,
          }}
        />
      </FilterBar>
    </UpgradePlanProvider>
  );
}

export default DiscoverabilityFilters;
